// 261005 E1-P1 — chặng ① Nguồn: nạp từ link (chỉ khi CHƯA có chương, như cũ) · "+ Thêm chương" · bảng chương thô · "Quét truyện".
// Tách từ `AdaptationSource.tsx` (:236-271 nạp link, :367-440 bảng chương). Giữ nguyên lời gọi API.
import { friendlyApiError } from '../jobErrorText';
import { rawChapterLocked } from '../jobTracking';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Checkbox, Dropdown, Input, InputNumber, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import Button from '../WbButton';
import { adaptationApi } from '../../../../services/api/adaptation';
import type { ProbeReport, RawChapter, ScanEstimate } from '../../../../types/adaptation';
import { additionalCrawlConfirmTitle, additionalCrawlParams, crawlConfirmTitle, MAX_CRAWL_CHAPTERS, crawlCountPlaceholder, normalizeMaxChapters, prefillUrl, probeTotalText } from '../../crawlOptionsText';
import { canEstimateFromProbe, nextChapterNumber, scanEstimateText, UNKNOWN_CHAPTERS_TEXT } from '../../scanCostText';
import { rawChapterReloadDisabledReason } from '../../sourceChapterUtils';
import StageFrame from '../StageFrame';
import { useCtaHolds, useStageIntent, useWorkbenchCtx } from '../WorkbenchContext';
import { dangerCopy } from '../workbenchView';
import { chapterGateNotes } from '../volumeGateView';
import { chapterTone, effectiveOnlyProblems, emptyChaptersText, filterProblemChapters, findingColor, findingDetailLines, findingShortLabel, hasNextChapter, LOCKED_TOOLTIP, optionsActionFor, problemCount, rawErrorCode, rawErrorText, repeatedJunkSummary, rowFindings, versionsRefreshKey } from '../../sourceChapterUtils';
import { ChapterVersionsPanel, ConfirmChapterOpModal, ReloadChapterModal, SplitChapterModal } from '../SourceChapterModals';

const { Text } = Typography;
const { TextArea } = Input;

const VERDICT: Record<string, { type: 'success' | 'warning' | 'error'; label: string }> = {
  USABLE: { type: 'success', label: 'Lấy được cả truyện' },
  PARTIAL: { type: 'warning', label: 'Chỉ lấy được một phần' },
  BLOCKED: { type: 'error', label: 'Không lấy được' },
};

const SourceStage: React.FC = () => {
  const { storyId, storyLink, dataVersion, data, workbench, refreshAll, busy, bgJob, stageState, requestAssistant, mainWidth, navRows, setTopMenuExtra } = useWorkbenchCtx();
  const stageCta = useCtaHolds().holds('stage');   // 3h3: đỏ chỉ khi không có dải giữ việc
  /** F36 — BE khoá sửa chương khi nạp/quét/dựng/bản địa hoá đang chạy (409 JOB_RUNNING); lấy từ danh sách job BE nên đúng cả sau tải lại. */
  const chapterLock = rawChapterLocked(workbench?.jobs.running);
  const gateNotes = chapterGateNotes(workbench?.newStoryRun?.volumeGate);   // 3d3: lỗi cổng ① quét quyển gắn vào chương (`chapter:N`) ⇒ nhãn ở cột «Tình trạng», không thêm khối UI
  const narrowTable = mainWidth < 1100;   // F21: dưới ~1100px dãy nút hàng gom vào "⋯" (7 nút không vừa cột cố định)
  const [url, setUrl] = useState('');
  const [chapterCount, setChapterCount] = useState<number | null>(null);   // test-dev F5: trống = nạp tất cả
  useEffect(() => { setUrl((cur) => prefillUrl(cur, storyLink)); }, [storyLink]);   // F2: điền sẵn link lúc tạo truyện, không đè chữ đã gõ
  const [probe, setProbe] = useState<ProbeReport | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id?: string; name: string; content: string; modifiedAt?: string } | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(false);          // 261005 E3-P4a
  const [reloadOf, setReloadOf] = useState<RawChapter | null>(null);
  const [splitOf, setSplitOf] = useState<RawChapter | null>(null);
  const [confirmOp, setConfirmOp] = useState<{ chapter: RawChapter; kind: 'merge' | 'delete' } | null>(null);
  const [versionsKey, setVersionsKey] = useState(0);
  const [autoScan, setAutoScan] = useState(true);               // 261005 E4-P3: "Tự quét sau khi nạp", mặc định bật
  const [probeEstimate, setProbeEstimate] = useState<ScanEstimate | null>(null);
  const [scanEstimate, setScanEstimate] = useState<ScanEstimate | null>(null);
  const [additionalCrawlOpen, setAdditionalCrawlOpen] = useState(false);
  const [additionalAutoScan, setAdditionalAutoScan] = useState(true);
  useStageIntent('source');

  const raws = data.rawChapters;
  const junk = repeatedJunkSummary(raws);   // F21: rác lặp dính nhiều chương ⇒ MỘT dòng ở đầu bảng
  const filtering = effectiveOnlyProblems(raws, onlyProblems);   // F2: hết chương có vấn đề ⇒ lọc tự hết hiệu lực
  useEffect(() => { if (onlyProblems && !filtering) setOnlyProblems(false); }, [onlyProblems, filtering]);
  const locked = stageState('source') === 'locked';
  const disabled = busy || !!working || locked;
  // Giá ước tính cho nút "Quét": chương của các quyển SẼ quét (BE tự tính). Tải lại khi số chương đổi hoặc job xong.
  const jobId = bgJob.bgJob?.id ?? null;
  useEffect(() => {
    if (raws.length === 0) { setScanEstimate(null); return; }
    let alive = true;
    adaptationApi.scanEstimate({ storyId }).then((r) => { if (alive) setScanEstimate(r); }).catch(() => { if (alive) setScanEstimate(null); });
    return () => { alive = false; };
  }, [storyId, raws.length, jobId, versionsKey]);
  const needScan = workbench?.stages.find((s) => s.key === 'source')?.blockers.some((b) => b.code === 'SCAN_NEEDED') ?? false;
  const staleBlocker = workbench?.stages.find((s) => s.key === 'source')?.blockers.find((b) => b.code === 'SCAN_NEEDED' && /cần quét lại/.test(b.message ?? ''));
  const afterChange = async () => { setVersionsKey((k) => k + 1); await refreshAll(); };

  // 3h2/C1 (smoke 26/10): chuỗi «Truyện mới» chưa chạy tới / đang quét ① ⇒ KHÔNG nút đỏ «Quét truyện» và không khối «Cần bạn xem»; quét riêng nằm trong menu ⋯ (đúng mockup `#sc=C&ph=0&s=1`).
  const srcRow = navRows?.find((r) => r.key === 'source');
  const chainHoldsScan = !!srcRow?.byChain && (srcRow.status === 'notRun' || srcRow.status === 'running');
  const startScan = async () => {
    if (locked) return;
    try { await bgJob.start('scan', () => adaptationApi.scan(storyId), 'Quét truyện'); }
    catch (e: any) { message.error(friendlyApiError(e, 'Không chạy được'), 8); }
  };
  const startScanRef = useRef(startScan);
  startScanRef.current = startScan;
  useEffect(() => {
    if (!chainHoldsScan || raws.length === 0) return undefined;
    setTopMenuExtra([{
      key: 'scan-own', label: 'Quét riêng bước này', disabled,
      onSelect: () => Modal.confirm({ title: 'Quét truyện?', content: 'Chạy nền, tốn AI — quét toàn bộ chương hiện có để kiểm tra và lập chỉ mục.', okText: 'Quét', cancelText: 'Thôi', onOk: () => startScanRef.current() }),
    }]);
    return () => setTopMenuExtra([]);
  }, [chainHoldsScan, raws.length, disabled, setTopMenuExtra]);
  const maxChapters = normalizeMaxChapters(chapterCount);
  const probeTotal = probeTotalText(probe);
  const startCrawl = async (startChapter?: number, autoScanAfterCrawl = autoScan): Promise<boolean> => {
    if (locked || !url.trim()) return false;
    try { return await bgJob.start('crawl', () => adaptationApi.crawl(storyId, url.trim(), maxChapters, autoScanAfterCrawl, startChapter), 'Nạp truyện'); }
    catch (e: any) { message.error(friendlyApiError(e, 'Không chạy được'), 8); return false; }
  };
  const doProbe = async () => {
    if (!url.trim()) { message.warning('Dán link trước đã'); return; }
    setWorking('probe');
    setProbeEstimate(null);
    try {
      const report = await adaptationApi.probe(url.trim());
      setProbe(report);
      // Có số chương ⇒ hỏi giá; không có ⇒ KHÔNG gọi (hiện câu "chưa rõ").
      if (canEstimateFromProbe(report.totalChapters)) {
        adaptationApi.scanEstimate({ chapters: report.totalChapters }).then((r) => setProbeEstimate(r)).catch(() => setProbeEstimate(null));
      }
    }
    catch (e: any) { message.error(friendlyApiError(e, 'Không dò được')); }
    finally { setWorking(null); }
  };
  const saveChapter = async () => {
    if (locked) return;
    if (!editing?.content.trim()) { message.warning('Chưa có nội dung'); return; }
    setWorking('save');
    try {
      if (editing.id) await adaptationApi.editRawChapter(storyId, editing.id, { name: editing.name, content: editing.content, expectedModifiedAt: editing.modifiedAt });
      else await adaptationApi.saveRawChapter(storyId, { name: editing.name, content: editing.content });
      setEditing(null);
      await afterChange();
    } catch (e: any) {
      if (rawErrorCode(e) === 'CHAPTER_CHANGED') { setEditing(null); await afterChange(); message.warning('Chương vừa đổi ở nơi khác — đã tải lại, thử lại', 6); }
      else message.error(rawErrorText(e, 'Không lưu được'));
    } finally { setWorking(null); }
  };
  const toggleAcceptedFinding = async (chapter: RawChapter, code: string, accepted: boolean) => {
    setWorking('accept-finding');
    try {
      await adaptationApi.rawChapterAcceptFinding(chapter.id, code, accepted);
      message.success(accepted ? 'Đã ghi nhận: chương này được chấp nhận là cụt ở nguồn.' : 'Đã bỏ chấp nhận — cảnh báo được bật lại.');
      await afterChange();
    } catch (e: any) { message.error(rawErrorText(e, 'Không đổi được trạng thái chấp nhận')); }
    finally { setWorking(null); }
  };

  const primary = (
    <>
      <Button id="btn-add-chapter" disabled={disabled} onClick={() => setEditing({ name: '', content: '' })}>+ Thêm chương</Button>
      {raws.length > 0 && (
        <Button id="btn-crawl-more" disabled={disabled} onClick={() => { setAdditionalAutoScan(autoScan); setAdditionalCrawlOpen(true); }}>Nạp thêm từ link</Button>
      )}
      {raws.length > 0 && !chainHoldsScan && (
        <Popconfirm title="Quét truyện?" description="Chạy nền, tốn AI — quét toàn bộ chương hiện có để kiểm tra và lập chỉ mục." okText="Quét" cancelText="Thôi" onConfirm={startScan}>
          <Button
            id="btn-scan" type={needScan ? 'primary' : 'default'} cta={needScan && stageCta} disabled={disabled}
            loading={bgJob.bgJob?.key === 'scan' || bgJob.starting === 'scan'}
          >Quét truyện</Button>
        </Popconfirm>
      )}
      {staleBlocker && <Tag color="orange" id="tag-scan-stale">{staleBlocker.message}</Tag>}
      {raws.length > 0 && scanEstimateText(scanEstimate) && <Text type="secondary" style={{ fontSize: 13 }}>{scanEstimateText(scanEstimate)}</Text>}
    </>
  );

  return (
    <StageFrame
      stage="source" primary={primary}
      redo={raws.length > 0 && !chainHoldsScan ? { label: 'Quét lại truyện', confirm: dangerCopy('rescan'), run: startScan, disabled } : undefined}
    >
      {raws.length === 0 && data.loadError && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 8 }} message="Không tải được danh sách chương"
          action={<Button size="small" onClick={() => { refreshAll(); }}>Thử lại</Button>}
        />
      )}
      {raws.length === 0 && !data.loadError && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div className="wb-crawl-row">
            <Input
              className="wb-crawl-url" placeholder="Dán link truyện (trang mục lục)" value={url} disabled={disabled}
              onChange={(e) => setUrl(e.target.value)} onPressEnter={() => { void startCrawl(); }}
            />
            <Tooltip title="Số chương nạp từ chương 1 trở đi. Để trống = nạp tất cả (tối đa 1000)">
              <InputNumber
                id="inp-crawl-count" className="wb-crawl-count" aria-label="Số chương" min={1} max={MAX_CRAWL_CHAPTERS} precision={0} controls={false}
                placeholder={crawlCountPlaceholder(probe)} value={chapterCount} disabled={disabled}
                onChange={(v) => setChapterCount(typeof v === 'number' ? v : null)} onPressEnter={() => { void startCrawl(); }}
              />
            </Tooltip>
            <div className="wb-crawl-btns">
              <Tooltip title="Kiểm trước xem lấy được bao nhiêu chương, có bị khoá không">
                <Button onClick={doProbe} loading={working === 'probe'} disabled={disabled}>Dò thử</Button>
              </Tooltip>
              <Popconfirm
                title={crawlConfirmTitle(maxChapters)} okText="Nạp" cancelText="Huỷ"
                disabled={!autoScan || !!probe} onConfirm={() => startCrawl()}
              >
                <Button
                  type="primary" cta={stageCta} loading={bgJob.bgJob?.key === 'crawl' || bgJob.starting === 'crawl'} disabled={disabled || !url.trim()}
                  onClick={() => { if (!autoScan || probe) startCrawl(); }}
                >Nạp truyện</Button>
              </Popconfirm>
            </div>
          </div>
          <Space size={8} wrap>
            <Switch size="small" checked={autoScan} onChange={setAutoScan} disabled={disabled} />
            <Text style={{ fontSize: 13 }}>Tự quét sau khi nạp</Text>
            {probe && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {canEstimateFromProbe(probe.totalChapters) ? (scanEstimateText(probeEstimate) ?? '') : UNKNOWN_CHAPTERS_TEXT}
              </Text>
            )}
          </Space>
          {probe && (
            <Alert
              type={VERDICT[probe.verdict]?.type ?? 'info'} showIcon
              message={`${VERDICT[probe.verdict]?.label ?? probe.verdict}${probeTotal ? ` · ${probeTotal}` : ''}`}
              description={<Text type="secondary" style={{ fontSize: 13 }}>{probe.reason}</Text>}
            />
          )}
        </Space>
      )}

      <Modal
        open={additionalCrawlOpen}
        title={additionalCrawlConfirmTitle(additionalCrawlParams(raws, maxChapters).startChapter, maxChapters)}
        okText="Nạp thêm" cancelText="Huỷ" confirmLoading={bgJob.starting === 'crawl'}
        okButtonProps={{ disabled: disabled || !url.trim() }}
        onCancel={() => setAdditionalCrawlOpen(false)}
        onOk={async () => {
          const params = additionalCrawlParams(raws, maxChapters, additionalAutoScan);
          const started = await startCrawl(params.startChapter, params.autoScan);
          if (started) setAdditionalCrawlOpen(false);
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">Nạp từ chương {additionalCrawlParams(raws, maxChapters).startChapter}; các chương đã có được giữ nguyên.</Text>
          <Input value={url} disabled={disabled} placeholder="Link trang mục lục" onChange={(e) => setUrl(e.target.value)} />
          <Tooltip title="Số chương mới cần nạp. Để trống = nạp tất cả phần còn lại (tối đa 1000).">
            <InputNumber
              id="inp-crawl-more-count" style={{ width: '100%' }} aria-label="Số chương nạp thêm" min={1} max={MAX_CRAWL_CHAPTERS} precision={0} controls={false}
              placeholder="Số chương mới" value={chapterCount} disabled={disabled}
              onChange={(v) => setChapterCount(typeof v === 'number' ? v : null)}
            />
          </Tooltip>
          <Checkbox checked={additionalAutoScan} disabled={disabled} onChange={(event) => setAdditionalAutoScan(event.target.checked)}>
            Tự quét nguyên liệu sau khi nạp (tốn AI)
          </Checkbox>
        </Space>
      </Modal>

      {raws.length > 0 && problemCount(raws) > 0 && (
        <Checkbox id="chk-only-problems" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} style={{ marginBottom: 8 }}>
          Chỉ chương có vấn đề ({problemCount(raws)})
        </Checkbox>
      )}
      {junk && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 8 }} message={junk.text}
          description={<Text type="secondary" style={{ fontSize: 13 }}>{junk.detail} Mở "Sửa" hoặc "Tải lại" từng chương để bỏ phần thừa.</Text>}
        />
      )}
      <Table
        rowKey="id" size="small" scroll={raws.length > 0 ? { x: narrowTable ? 560 : 900 } : undefined} dataSource={filterProblemChapters(raws, filtering)}
        locale={{ emptyText: emptyChaptersText(raws.length, onlyProblems, data.loadError) }}
        pagination={raws.length > 10 ? { pageSize: 10, size: 'small' } : false}
        columns={[
          { title: '#', dataIndex: 'orderIndex', key: 'orderIndex', width: 46 },
          { title: 'Tên', dataIndex: 'name', key: 'name', width: narrowTable ? 110 : 150, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <Tooltip title={v} placement="topLeft"><span>{v}</span></Tooltip> : null) },   // F5: đủ rộng, ellipsis + tooltip tên đầy đủ
          {
            title: 'Độ dài', key: 'len', width: 72, align: 'right' as const,
            render: (_: unknown, r: RawChapter) => {
              const n = (r.content ?? '').length;
              // Màu theo `severity` BE dò được (không gõ cứng ngưỡng ở FE).
              return <Text style={{ fontSize: 13, color: findingColor(chapterTone(r)) }} type={chapterTone(r) ? undefined : 'secondary'}>{n.toLocaleString()}</Text>;
            },
          },
          {
            title: 'Tình trạng', key: 'status', width: narrowTable ? 200 : 240,
            render: (_: unknown, r: RawChapter) => {
              // F21: nhãn NGẮN theo mức (đỏ/cam); câu đầy đủ + đoạn trích + chương liên quan nằm trong tooltip → không bao giờ tràn sang cột nút.
              const fs = rowFindings(r, !!junk);
              const shown = fs.slice(0, 2);
              return (
                <div className="wb-chap-status">
                  {r.lockedByScript && <Tooltip title={LOCKED_TOOLTIP}><Tag>🔒 đã viết</Tag></Tooltip>}
                  {shown.map((f, i) => (
                    <Tooltip key={`${f.code}-${i}`} placement="topLeft" title={<div>{findingDetailLines(f).map((l, j) => <div key={j}>{l}</div>)}</div>}>
                      <Tag color={f.accepted ? 'green' : f.severity === 'ERROR' ? 'red' : 'gold'}>{f.accepted ? 'Đã chấp nhận' : findingShortLabel(f)}</Tag>
                    </Tooltip>
                  ))}
                  {fs.length > shown.length && (
                    <Tooltip title={<div>{fs.slice(shown.length).flatMap((f) => findingDetailLines(f)).map((l, j) => <div key={j}>{l}</div>)}</div>}><Tag>+{fs.length - shown.length}</Tag></Tooltip>
                  )}
                  {gateNotes.has(r.orderIndex) && (
                    <Tooltip placement="topLeft" title={<div>{gateNotes.get(r.orderIndex)!.lines.map((l, j) => <div key={j}>{l}</div>)}</div>}>
                      <Tag color="red">{gateNotes.get(r.orderIndex)!.label}</Tag>
                    </Tooltip>
                  )}
                  {fs.length === 0 && junk && (r.findings ?? []).length > 0 && <Text type="secondary" style={{ fontSize: 13 }}>rác lặp (xem trên)</Text>}
                </div>
              );
            },
          },
          {
            title: '', key: 'act', width: narrowTable ? 110 : 400,
            render: (_: unknown, r: RawChapter) => {
              const opt = optionsActionFor(r);   // 261005 E3-P4b1
              const lockedBtn = (el: React.ReactElement<any>) => (r.lockedByScript ? <Tooltip title={LOCKED_TOOLTIP}><span>{React.cloneElement(el, { disabled: true })}</span></Tooltip> : el);
              const reloadDis = locked || r.lockedByScript || disabled || !r.link;
              const reloadReason = rawChapterReloadDisabledReason(r);
              const splitDis = locked || r.lockedByScript || disabled;
              const mergeDis = splitDis || !hasNextChapter(raws, r.id);
              const truncation = (r.findings ?? []).find((f) => f.code === 'ENDS_MID_SENTENCE' || f.code === 'ENDS_ELLIPSIS');
              const acceptButton = truncation ? (
                truncation.accepted ? <Tooltip title={`Đã chấp nhận bởi ${truncation.accepted.acceptedBy} lúc ${new Date(truncation.accepted.acceptedAt).toLocaleString()}`}><Button size="small" type="link" disabled={disabled} onClick={() => toggleAcceptedFinding(r, truncation.code, false)}>Bỏ chấp nhận</Button></Tooltip>
                  : <Popconfirm title="Chấp nhận chương cụt?" description="Xác nhận đây là nội dung cụt từ nguồn. Cảnh báo sẽ tự bật lại nếu nội dung chương đổi." okText="Chấp nhận" cancelText="Huỷ" onConfirm={() => toggleAcceptedFinding(r, truncation.code, true)}><Button size="small" type="link" disabled={disabled}>Chấp nhận</Button></Popconfirm>
              ) : null;
              if (narrowTable) {
                return (
                  <Space size={0} wrap>
                    {lockedBtn(<Button size="small" type="link" disabled={locked || chapterLock} onClick={() => setEditing({ id: r.id, name: r.name ?? '', content: r.content ?? '', modifiedAt: r.modifiedAt })}>Sửa</Button>)}
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          ...(opt ? [{ key: 'options', label: opt.disabledReason ? `Phương án (tốn AI) — ${opt.disabledReason}` : 'Phương án (tốn AI)', disabled: locked || !!opt.disabledReason }] : []),
                          { key: 'reload', label: reloadReason ? `Tải lại — ${reloadReason}` : 'Tải lại', disabled: reloadDis },
                          { key: 'split', label: 'Tách', disabled: splitDis },
                          { key: 'merge', label: 'Gộp sau', disabled: mergeDis },
                          { key: 'delete', label: 'Xoá', danger: true, disabled: splitDis },
                          ...(truncation ? [{ key: 'accept', label: truncation.accepted ? 'Bỏ chấp nhận' : <Popconfirm title="Chấp nhận chương cụt?" description="Xác nhận đây là nội dung cụt từ nguồn. Cảnh báo sẽ tự bật lại nếu nội dung chương đổi." okText="Chấp nhận" cancelText="Huỷ" onConfirm={() => toggleAcceptedFinding(r, truncation.code, true)}><span>Chấp nhận chương cụt</span></Popconfirm>, disabled }] : []),
                        ],
                        onClick: ({ key }) => {
                          if (key === 'options' && opt) requestAssistant(opt.intent);
                          else if (key === 'reload') setReloadOf(r);
                          else if (key === 'split') setSplitOf(r);
                          else if (key === 'merge') setConfirmOp({ chapter: r, kind: 'merge' });
                          else if (key === 'accept' && truncation?.accepted) void toggleAcceptedFinding(r, truncation.code, false);
                          else if (key === 'accept') return;
                          else setConfirmOp({ chapter: r, kind: 'delete' });
                        },
                      }}
                    >
                      <Button size="small" type="link" aria-label="Thêm thao tác của chương">⋯</Button>
                    </Dropdown>
                  </Space>
                );
              }
              return (
                <Space size={0} wrap>
                  {lockedBtn(<Button size="small" type="link" disabled={locked || chapterLock} onClick={() => setEditing({ id: r.id, name: r.name ?? '', content: r.content ?? '', modifiedAt: r.modifiedAt })}>Sửa</Button>)}
                  {lockedBtn(reloadReason ? (
                    <Tooltip title={reloadReason}><span><Button size="small" type="link" disabled>Tải lại</Button></span></Tooltip>
                  ) : <Button size="small" type="link" disabled={reloadDis} onClick={() => setReloadOf(r)}>Tải lại</Button>)}
                  {lockedBtn(<Button size="small" type="link" disabled={disabled} onClick={() => setSplitOf(r)}>Tách</Button>)}
                  {lockedBtn(<Button size="small" type="link" disabled={disabled || !hasNextChapter(raws, r.id)} onClick={() => setConfirmOp({ chapter: r, kind: 'merge' })}>Gộp sau</Button>)}
                  {lockedBtn(<Button size="small" type="link" danger disabled={disabled} onClick={() => setConfirmOp({ chapter: r, kind: 'delete' })}>Xoá</Button>)}
                  {acceptButton}
                  {opt && (
                    <Tooltip title={opt.disabledReason ?? 'Mở khung Trợ lý, gửi lỗi của chương và nhờ AI đưa tới 3 phương án khác cách. Tốn AI; chưa ghi gì cho tới khi bạn bấm Áp.'}>
                      <span><Button size="small" type="link" className="btn-chapter-options" disabled={locked || !!opt.disabledReason} onClick={() => requestAssistant(opt.intent)}>Phương án (tốn AI)</Button></span>
                    </Tooltip>
                  )}
                </Space>
              );
            },
          },
        ] as any}
      />

      <ChapterVersionsPanel storyId={storyId} refreshKey={versionsRefreshKey(versionsKey, dataVersion, raws.length)} disabled={disabled} onChanged={afterChange} />
      <ReloadChapterModal chapter={reloadOf} onClose={() => setReloadOf(null)} onChanged={afterChange} />
      <SplitChapterModal chapter={splitOf} onClose={() => setSplitOf(null)} onChanged={afterChange} />
      <ConfirmChapterOpModal chapter={confirmOp?.chapter ?? null} kind={confirmOp?.kind ?? 'delete'} onClose={() => setConfirmOp(null)} onChanged={afterChange} />

      <Modal
        open={!!editing} title={editing?.id ? 'Sửa chương' : 'Thêm chương'}
        onCancel={() => setEditing(null)} onOk={saveChapter} confirmLoading={working === 'save'} width={760}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {!editing?.id && <Text type="secondary" style={{ fontSize: 13 }}>Sẽ là chương {nextChapterNumber(raws)} (xếp cuối)</Text>}
          <Input
            placeholder="Tên chương (không bắt buộc)" value={editing?.name ?? ''}
            onChange={(e) => setEditing((s) => (s ? { ...s, name: e.target.value } : s))}
          />
          <TextArea
            rows={16} placeholder="Dán nội dung chương vào đây" value={editing?.content ?? ''}
            onChange={(e) => setEditing((s) => (s ? { ...s, content: e.target.value } : s))}
          />
        </Space>
      </Modal>
    </StageFrame>
  );
};

export default SourceStage;
